<script setup lang="ts">
import type { ItemMedia } from '@/components/common/successfulModal/SuccessfulItemsMedia.vue'
import type { ShareProp } from '@/components/common/successfulModal/SuccessfulModalBody.vue'
import type { MintedNFT, MintingSession } from '@/components/drop/types'

const props = defineProps<{
  mintingSession: MintingSession
}>()

const { $i18n } = useNuxtApp()
const { add: toast } = useToast()
const { prefix } = usePrefix()
const { accountId } = useAuth()
const { toMintNFTs } = storeToRefs(useDropStore())

const txHash = computed(() => props.mintingSession.txHash ?? '')
const singleMint = computed(() => props.mintingSession.items.length === 1)
const mintedNft = computed<MintedNFT | undefined>(
  () => props.mintingSession.items[0],
)

const itemMedias = props.mintingSession.items.map(item => ({
  id: item.id,
  name: item.name,
  image: item.image,
  collection: item.collection.id,
  collectionName: item.collection.name,
  mimeType: item.mimeType,
  metadata: item.metadata,
}))
const items = ref<ItemMedia[]>(itemMedias)
const isLoading = ref(true)

onMounted(async () => {
  toast({ title: 'NFT minted successfully! It may take a minute to appear on the website.', duration: 15000 })

  // update serial number in nft.name asynchronously
  const metadatas = await Promise.all(
    items.value.map(item => $fetch<{ name?: string }>(item.metadata)),
  )

  items.value.forEach((_, index) => {
    const metadata = metadatas[index]
    if (metadata?.name && items.value[index] && toMintNFTs.value[index]) {
      items.value[index].name = metadata.name
      toMintNFTs.value[index].name = metadata.name
    }
  })
  isLoading.value = false
})

const nftPath = computed(
  () => mintedNft.value
    ? `/${mintedNft.value.chain}/gallery/${mintedNft.value.collection.id}-${mintedNft.value.id}`
    : '',
)
const nftFullUrl = computed(() => `${window.location.origin}${nftPath.value}`)
const userProfilePath = computed(() => `/${prefix.value}/u/${accountId.value}`)

const getItemSn = (name: string) => `#${name.split('#')[1]}`

const sharingTxt = computed(() =>
  singleMint.value
    ? $i18n.t('sharing.dropNft', [items.value[0]?.collectionName, getItemSn(items.value[0]?.name || '')])
    : $i18n.t('sharing.dropNfts', [items.value[0]?.collectionName, items.value.map(item => getItemSn(item.name)).join(', ')]),
)

const share = computed<ShareProp>(() => ({
  disabled: isLoading.value,
  text: sharingTxt.value,
  url: nftFullUrl.value,
  withCopy: singleMint.value,
}))

const viewButton = computed(() => ({
  label: $i18n.t('general.viewNft', props.mintingSession.items.length),
  onClick: handleViewNft,
}))

const actionButtons = computed(() => ({ primary: viewButton.value }))

function handleViewNft() {
  window.open(
    singleMint.value ? nftPath.value : userProfilePath.value,
    '_blank',
  )
}
</script>

<template>
  <SuccessfulModalBody
    :tx-hash="txHash"
    :share="share"
    :status="mintingSession.status"
    :action-buttons="actionButtons"
  >
    <SuccessfulItemsMedia
      :header="{
        single: $t('drop.youSuccessfullyClaimedNft', [1]),
        multiple: $t('drop.amountMintedSuccessfully', [items.length]),
      }"
      :items="items"
    />
  </SuccessfulModalBody>
</template>
